import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { AlertErrorCircle, InfoCircle, WarningTriangle } from '@/assets/icons'

export type InlineMessageVariant = 'error' | 'neutral' | 'warning' | 'info'

/* Shared by every variant; the config below only carries what differs. */
const sharedContainer = 'flex items-start gap-1 overflow-hidden rounded px-2 py-2 text-xs font-medium leading-4'

type VariantStyle = {
  /* Variant-specific classes (background, text color, and any padding override) layered onto sharedContainer. */
  container: string
  role: 'alert' | 'status'
  /* Leading icon for the variant; null for neutral, which has none by default. */
  defaultIcon: ReactNode
}

const variantStyles: Record<InlineMessageVariant, VariantStyle> = {
  error: { container: 'bg-red-50 text-red-800', role: 'alert', defaultIcon: <AlertErrorCircle /> },
  info: { container: 'bg-brand-50 text-brand-700', role: 'status', defaultIcon: <InfoCircle /> },
  warning: { container: 'bg-yellow-50 text-yellow-800', role: 'status', defaultIcon: <WarningTriangle /> },
  neutral: { container: 'bg-surface-subtle px-3 text-text-tertiary', role: 'status', defaultIcon: null },
}

export type InlineMessageProps = {
  /** @default 'neutral' */
  variant?: InlineMessageVariant
  children: ReactNode
  /** Overrides the variant's default leading icon. Pass `null` to render no icon. */
  icon?: ReactNode
  /** Merged onto the variant's base styles for one-off overrides (e.g. adding a border). */
  className?: string
}

/**
 * Compact inline status text.
 *
 * - **neutral** (default) — low-emphasis note
 * - **error** — validation / failure
 * - **warning** — caution without hard error
 * - **info** — neutral heads-up
 */
export function InlineMessage({ variant = 'neutral', children, icon, className }: InlineMessageProps) {
  const style = variantStyles[variant]
  const leadingIcon = icon !== undefined ? icon : style.defaultIcon
  // Neutral's default padding is roomier for text alone; when it gains an icon, tighten it so the icon lines up with the other variants.
  const iconPadding = variant === 'neutral' && leadingIcon != null ? 'px-2' : undefined

  return (
    <div
      data-ds-component="InlineMessage"
      data-ds-variant={variant}
      className={twMerge(sharedContainer, style.container, iconPadding, className)}
      role={style.role}
    >
      {/* Decorative icon, kept on the first line when the message wraps. */}
      {leadingIcon != null && (
        <span className="mt-px flex shrink-0 [&_svg]:h-3.5 [&_svg]:w-3.5" aria-hidden>
          {leadingIcon}
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}
